import { OrdersStatuses, Trade } from 'Models/ordersHistory';
import TradingApi from 'Apis/Trading';
import SpotTradesRequest from 'Lib/entities/privatePresenter/SpotTradesRequest';
import SpotOrdersRequest from 'Lib/entities/privatePresenter/SpotOrdersRequest';
import CancelOrderRequest from 'Lib/entities/orderRegistrator/CancelOrderRequest';
import { parsePaginationHeaders } from 'Lib/utils/PaginationParser';
import SpotTrade, { ISpotTrade } from 'Entities/privatePresenter/SpotTrade';
import { OTC_PLACEMENT } from 'Const/otc';
import SpotOrderPresenter, { ISpotOrderPresenter } from 'Entities/privatePresenter/SpotOrderPresenter';
import { withQueue } from 'Store/utils';

export enum OrdersHistoryTypes {
    SPOT = 'SPOT',
    FUTURES = 'FUTURES',
}

const state = {
    orders: [] as SpotOrderPresenter[],
    activeOrders: [] as SpotOrderPresenter[],
    closedOrders: [] as SpotOrderPresenter[],
    trades: [] as SpotTrade[],
    activeSide: OrdersHistoryTypes.SPOT as OrdersHistoryTypes,
    notificationsCache: [],
    activePage: 1,
    itemsPerPage: 11,
    isRecordsDownloading: false,
    activeTerminalModuleTabIndex: 0,
    isSubscribedToOrdersUpdates: false,
    isSubscribedToTradesUpdates: false,
};

type HistoryState = typeof state;

const getters = {
    orders: (state) => state.orders,
    trades: (state) => state.trades,
    lastOrderCreatedAt: (state, getters) => new Date(getters.orders[0]?.createdAt || 0).valueOf(),
    ordersIds: (state, getters) => getters.orders.map(({ id }) => id),
    toggleAccountUpdaterID: (state) => state.toggleAccountUpdaterID,
    isToggleAccountUpdaterActive: (state, getters) => !!getters.toggleAccountUpdaterID,
    activePage: (state) => state.activePage,
    itemsPerPage: (state) => state.itemsPerPage,
    pagesCount: (state) => state.pagesCount,
    totalItemsCount: (state) => state.totalItemsCount,
    hasListActiveOrder: (state, getters) => getters.orders.some((order) => order.isActive),
    ordersListUpdaterID: (state) => state.ordersListUpdaterID,
    isOrdersListUpdaterActive: (state, getters) => getters.ordersListUpdaterID !== null,
    isRecordsDownloading: (state) => state.isRecordsDownloading,
    activeTerminalModuleTabIndex: (state) => state.activeTerminalModuleTabIndex,
    isActiveTabActive: (state, getters) => getters.activeTerminalModuleTabIndex === 1,
    isClosedTabActive: (state, getters) => getters.activeTerminalModuleTabIndex === 2,
    isTradesTabActive: (state, getters) => getters.activeTerminalModuleTabIndex === 3,
    isPositionsTabActive: (state, getters) => getters.activeTerminalModuleTabIndex === 4,
    isFundingsTabActive: (state, getters) => getters.activeTerminalModuleTabIndex === 5,
    activeUpdatersIds: (state) => state.activeUpdatersIds,
    isListUpdating: (state, getters) => getters.activeUpdatersIds.length > 0,
    currentOrdersUpdaterId: (state) => state.currentOrdersUpdaterId,
    // OTC
    ordersOTC: (state: HistoryState) => state.orders.filter((o) => o.placementName === OTC_PLACEMENT),
    tradesOTC: (state: HistoryState) => state.trades.filter((o) => o.placementName === OTC_PLACEMENT),
    activeOrdersOTC: (state: HistoryState) => state.activeOrders.filter((o) => o.placementName === OTC_PLACEMENT),
    closedOrdersOTC: (state: HistoryState) => state.closedOrders.filter((o) => o.placementName === OTC_PLACEMENT),
};

const mutations = {
    SET_ACTIVE_PAGE_NUMBER(state, pageNumber) {
        state.activePage = pageNumber;
    },
    SET_PAGES_COUNT(state, pagesCount) {
        state.pagesCount = pagesCount;
    },
    SET_TOTAL_ITEMS_COUNT(state, itemsCount) {
        state.totalItemsCount = itemsCount;
    },
    SET_NOTIFICATION_TO_CACHE(state, data) {
        state.notificationsCache.push(data);
    },
    ADD_ORDER(state, order) {
        state.orders.unshift(order);

        state.activeOrders = state.orders
            .filter(({ status }) => status.toLowerCase() === 'registered' || status.toLowerCase() === 'partially_filled' || status.toLowerCase() === 'placed');
        state.closedOrders = state.orders
            .filter(({ status }) => status.toLowerCase() !== 'registered' && status.toLowerCase() !== 'partially_filled');
    },
    UPDATE_ORDER(state, { index, order }) {
        state.orders.splice(index, 1, order);

        state.activeOrders = state.orders
            .filter(({ status }) => status.toLowerCase() === 'registered' || status.toLowerCase() === 'partially_filled' || status.toLowerCase() === 'placed');
        state.closedOrders = state.orders
            .filter(({ status }) => status.toLowerCase() !== 'registered' && status.toLowerCase() !== 'partially_filled');
    },
    ADD_TRADE(state, trade) {
        state.trades.unshift(trade);
    },
    UPDATE_TRADE(state, { index, trade }) {
        state.trades.splice(index, 1, trade);
    },
    SET_ORDER_TRADES(state, { orderId, trades }) {
        const currentOrder = state.orders.find((order) => order.id === orderId);

        if (currentOrder) {
            currentOrder.setTrades(trades);
        } else {
            throw `Order with { id: ${orderId} } is not exist`;
        }
    },
    SET_TRADES(state, trades) {
        state.trades = trades.map((trade) => new Trade(trade));
    },
    SET_TOGGLE_ACCOUNT_UPDATER_ID(state, id) {
        state.toggleAccountUpdaterID = id;
    },
    SET_ORDERS_LIST_UPDATER_ID(state, id) {
        state.ordersListUpdaterID = id;
    },
    SET_IS_RECORDS_DOWNLOADING(state, value) {
        state.isRecordsDownloading = value;
    },
    SET_ACTIVE_TERMINAL_MODULE_TAB_INDEX(state, index) {
        state.activeTerminalModuleTabIndex = index;
    },
    SET_CURRENT_ORDER_UPDATER_UD(state, id) {
        state.currentOrdersUpdaterId = id;
    },
    SET_ACTIVE_SIDE(state, side) {
        state.activeSide = side;
    },
};

const actions = {
    setActiveTerminalModuleTabIndex({ commit }, index) {
        commit('SET_ACTIVE_TERMINAL_MODULE_TAB_INDEX', index);
        commit('SET_ACTIVE_PAGE_NUMBER', 1);
    },
    downloadOrders: withQueue(({ dispatch }, shouldStop: { shouldStop: boolean; }) => {
        return dispatch('getOrders', shouldStop);
    }),
    async getOrders({ state, rootGetters, commit, dispatch }, stop: { shouldStop: boolean; }) {
        if (!state.isSubscribedToOrdersUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch('downloadOrders');
                },
            }, { root: true });
            state.isSubscribedToOrdersUpdates = true;
        }
        state.orders = [];
        if (stop.shouldStop) {
            return Promise.resolve();
        }
        commit('SET_ACTIVE_PAGE_NUMBER', 1);
        const { data: orders, headers } = await TradingApi.privateGetSpotOrders(new SpotOrdersRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            page: 1,
            perPage: 100,
            includeTotal: true,
        }), true);
        if (stop.shouldStop) {
            return Promise.resolve();
        }
        state.orders = [...state.orders, ...orders];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraOrders } = await TradingApi.privateGetSpotOrders(new SpotOrdersRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        page: i,
                        perPage: 100,
                    }));
                    if (stop.shouldStop) {
                        return Promise.resolve();
                    }
                    state.orders = [...state.orders, ...extraOrders];
                }
            }
        }
        state.activeOrders = state.orders
            .filter(({ status }) => status.toLowerCase() === 'registered' || status.toLowerCase() === 'partially_filled' || status.toLowerCase() === 'placed');
        state.closedOrders = state.orders
            .filter(({ status }) => status.toLowerCase() !== 'registered' && status.toLowerCase() !== 'partially_filled');
    },
    async downloadTrades({ state, rootGetters, commit, dispatch }) {
        if (!state.isSubscribedToTradesUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch('downloadTrades');
                },
            }, { root: true });
            state.isSubscribedToTradesUpdates = true;
        }
        commit('SET_ACTIVE_PAGE_NUMBER', 1);
        let allTrades: SpotTrade[] = [];
        const { data: trades, headers } = await TradingApi.privateGetSpotTrades(new SpotTradesRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            page: 1,
            perPage: 100,
            includeTotal: true,
        }));
        allTrades = trades;
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraTrades } = await TradingApi.privateGetSpotTrades(new SpotTradesRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        page: i,
                        perPage: 100,
                    }));
                    allTrades = [...allTrades, ...extraTrades];
                }
            }
        }
        state.trades = allTrades;
    },
    updateOrderBySocket({ getters, rootGetters, dispatch, state, commit }, order: ISpotOrderPresenter) {
        const notification = state.notificationsCache.find((n) => n.id === order.id && n.status === order.status);
        if (!notification) {
            commit('SET_NOTIFICATION_TO_CACHE', { id: order.id, status: order.status });
            if (order.status.toLowerCase() === 'canceled' || order.status.toLowerCase() === 'rejected') {
                dispatch('Notificator/showWarningNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            } else {
                dispatch('Notificator/showSuccessNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            }
        }

        if (rootGetters['Accounts/activeAccountID'] === order.accountId) {
            const index = getters.orders.findIndex(({ id }) => id === order.id);
            if (index === -1) {
                commit('ADD_ORDER', new SpotOrderPresenter(order));
            } else if (
                getters.orders[index].status !== OrdersStatuses.CANCELED.value
                && getters.orders[index].status !== OrdersStatuses.REJECTED.value
                && getters.orders[index].status !== OrdersStatuses.FILLED.value
            ) {
                commit('UPDATE_ORDER', {
                    index,
                    order: new SpotOrderPresenter(order),
                });
            }
        }
    },
    updateTradeBySocket({ getters, rootGetters, commit }, trade: ISpotTrade) {
        if (rootGetters['Accounts/activeAccountID'] === trade.accountId) {
            const index = getters.trades.findIndex(({ id }) => id === trade.id);
            if (index === -1) {
                commit('ADD_TRADE', new SpotTrade(trade));
            } else {
                commit('UPDATE_TRADE', {
                    index,
                    trade: new SpotTrade(trade),
                });
            }
        }
    },
    cancelOrder(_, orderId) {
        return TradingApi.privateOrdersCancelOrder(new CancelOrderRequest({
            id: orderId,
        }));
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
